import React, { useEffect, useState } from "react";
import { ComponentProps } from "../../../types";
import { SectionWrapper } from "../../containers/SectionWrapper";
import * as styles from "./ArboretumSearchResults.module.css";
import {
  InstantSearch,
  Configure,
  InfiniteHits,
  connectStateResults,
  RefinementList,
  Panel,
  MenuSelect,
  CurrentRefinements,
  ClearRefinements,
} from "react-instantsearch-dom";
import { searchIndex, searchClient } from "../../../lib/algolia";
import DebouncedSearchBox from "../../elements/SearchInput/DebouncedSearchBox";
import { SearchState } from "react-instantsearch-core";
import { CustomRichTextElement } from "../../elements/CustomRichTextElement/CustomRichTextElement";
import { formatImages } from "../../../lib/images";
import { KontentImageElement } from "../../elements/KontentImageElement/KontentImageElement";
import { TreeStamp } from "../../elements/TreeStamp/TreeStamp";
import { CollapsiblePanel } from "./CollapsiblePanel";
import { useBreakpoint } from "gatsby-plugin-breakpoints";
import CloseButton from "../../elements/CloseButton/CloseButton";

interface Props extends ComponentProps {
  searchTerm?: SearchState;
  onSearchTermChange?: (arg: string) => void;
  createURL?: (arg: string) => string;
  collectionTooltip?: string;
  averageTreeSizeTooltip?: string;
  familyTooltip?: string;
  yearPlantedTooltip?: string;
  growthRateTooltip?: string;
  sunRequirementsTooltip?: string;
  waterRequirementsTooltip?: string;
  soilRequirementsTooltip?: string;
  nativeRangeTooltip?: string;
  hardinessZoneTooltip?: string;
  seasonOfInterestTooltip?: string;
  supportedWildlifeTooltip?: string;
}

interface AlgoliaMatch {
  value: string;
  matchLevel: string;
  matchedWords: string[];
}
interface SearchResult {
  url: string;
  title: string;
  objectId: string;
  description: string;
  contentCategory: string;
  content: any; // page content components
  _hightlightResult: {
    url: AlgoliaMatch;
    title: AlgoliaMatch;
    description: AlgoliaMatch;
    contentCategory: string;
  };
  treeId: string;
  image?: {
    system: {
      type: "media___image";
    };
    elements: {
      alt_text: {
        value: string;
      };
      image?: {
        value?: {
          description: string;
          width: number;
          height: number;
          url: string;
          name: string;
          size: number;
        }[];
      };
      small_image?: {
        value?: {
          description: string;
          width: number;
          height: number;
          url: string;
          name: string;
          size: number;
        }[];
      };
      caption: {
        value?: string;
      };
    };
  };
  scientificName?: string;
  commonName?: string;
}

interface HitItem {
  hit: SearchResult;
}

export const ArboretumSearchResults: React.FC<Props> = ({
  collectionTooltip,
  averageTreeSizeTooltip,
  familyTooltip,
  yearPlantedTooltip,
  growthRateTooltip,
  sunRequirementsTooltip,
  waterRequirementsTooltip,
  soilRequirementsTooltip,
  nativeRangeTooltip,
  hardinessZoneTooltip,
  seasonOfInterestTooltip,
  supportedWildlifeTooltip,
  ...props
}) => {
  const bp = useBreakpoint();
  const [showFilters, setShowFilters] = useState(bp.md);

  useEffect(() => {
    if (bp.md) {
      setShowFilters(true);
    }
  }, [bp]);

  const ResultWrapper = connectStateResults(({ searchResults, children }) => {
    const currentPage = searchResults?.page + 1;
    const isLastPage =
      currentPage * searchResults?.hitsPerPage > searchResults?.nbHits;

    return searchResults && searchResults.nbHits !== 0 ? (
      <div className={styles.results}>
        <div className={styles.resultsInfo}>
          <span className={styles.totalHits}>
            {searchResults.nbHits} results{" "}
          </span>
          | Showing{" "}
          {isLastPage
            ? searchResults.nbHits
            : currentPage * searchResults.hitsPerPage}{" "}
          of {searchResults.nbHits}
        </div>
        {children}
      </div>
    ) : (
      <div className={styles.resultsInfo}>
        {searchResults?.query &&
          `No results found for ${searchResults?.query} `}
      </div>
    );
  });

  const Hit = (props: HitItem) => {
    const { title, scientificName, treeId, commonName, url } = props.hit;
    const image =
      props.hit.image && formatImages(props.hit.image?.elements).largeImage;

    return (
      <TreeStamp
        name={commonName || title}
        treeId={treeId}
        species={scientificName}
        image={image}
        clickable={true}
        cta={{
          label: "View Details",
          href: url,
          target: "_self",
        }}
        style="listing"
      />
    );
  };

  return (
    <SectionWrapper {...props}>
      <div className={styles.arboretumsearchresults}>
        <InstantSearch
          indexName={searchIndex.arboretum}
          searchClient={searchClient}
          searchState={props.searchTerm}
          onSearchStateChange={props.onSearchTermChange}
          createURL={props.createURL}
        >
          <Configure hitsPerPage={20} />
          <div
            className={`${styles.filterContainer} ${
              showFilters ? styles.open : ""
            }`}
          >
            <div className={`${styles.panel} ${styles.filtersHeader}`}>
              <h2>Filters</h2>
              {!bp.md && <CloseButton onClick={() => setShowFilters(false)} />}
            </div>
            <CollapsiblePanel
              header={"Collection"}
              attribute="collections"
              tooltip={collectionTooltip}
              open={!!props.searchTerm?.refinementList?.collections}
            />
            <CollapsiblePanel
              header={"Average Tree Size"}
              attribute="averageTreeSize"
              tooltip={averageTreeSizeTooltip}
              open={!!props.searchTerm?.refinementList?.averageTreeSize}
            />
            <CollapsiblePanel
              header={"Family"}
              attribute="family"
              tooltip={familyTooltip}
              open={!!props.searchTerm?.refinementList?.family}
            />
            <CollapsiblePanel
              header={"Year Planted"}
              attribute="yearPlanted"
              tooltip={yearPlantedTooltip}
              open={!!props.searchTerm?.refinementList?.yearPlanted}
            />
            <CollapsiblePanel
              header={"Growth Rate"}
              attribute="growthRate"
              tooltip={growthRateTooltip}
              open={!!props.searchTerm?.refinementList?.growthRate}
            />
            <CollapsiblePanel
              header={"Sun Requirements"}
              attribute="sunRequirements"
              tooltip={sunRequirementsTooltip}
              open={!!props.searchTerm?.refinementList?.sunRequirements}
            />
            <CollapsiblePanel
              header={"Water Requirements"}
              attribute="waterRequirements"
              tooltip={waterRequirementsTooltip}
              open={!!props.searchTerm?.refinementList?.waterRequirements}
            />
            <CollapsiblePanel
              header={"Soil Requirements"}
              attribute="soilRequirements"
              tooltip={soilRequirementsTooltip}
              open={!!props.searchTerm?.refinementList?.soilRequirements}
            />
            <CollapsiblePanel
              header={"Native Range"}
              attribute="nativeRange"
              tooltip={nativeRangeTooltip}
              open={!!props.searchTerm?.refinementList?.nativeRange}
            />
            <CollapsiblePanel
              header={"Hardiness Zone"}
              attribute="hardinessZone"
              tooltip={hardinessZoneTooltip}
              open={!!props.searchTerm?.refinementList?.hardinessZone}
            />
            <CollapsiblePanel
              header={"Season of Interest"}
              attribute="seasonOfInterest"
              tooltip={seasonOfInterestTooltip}
              open={!!props.searchTerm?.refinementList?.seasonOfInterest}
            />
            <CollapsiblePanel
              header={"Supported Wildlife"}
              attribute="supportedWildlife"
              tooltip={supportedWildlifeTooltip}
              open={!!props.searchTerm?.refinementList?.supportedWildlife}
            />
          </div>
          <div className={styles.resultsContainer}>
            <DebouncedSearchBox placeHolder="Search Arboretum..." delay={200} />
            {!bp.md && (
              <div className={styles.advancedBtnWrap}>
                <button
                  onClick={() => setShowFilters(true)}
                  className={styles.advancedBtn}
                  type="button"
                >
                  <span>+ </span>
                  <span>ADVANCED FILTERS</span>
                </button>
              </div>
            )}
            <div className={styles.refinements}>
              <CurrentRefinements
                transformItems={(items: any) =>
                  items.map((item: any) => {
                    const attribute =
                      item.attribute.charAt(0).toUpperCase() +
                      item.attribute.replace(/([A-Z])/g, " $1").slice(1);
                    item.label = attribute.trim() + ": ";
                    return item;
                  })
                }
              />
              <ClearRefinements
                translations={{
                  reset: "Clear Filters",
                }}
              />
            </div>
            <ResultWrapper>
              <div className={styles.results}>
                <InfiniteHits
                  hitComponent={Hit}
                  translations={{
                    loadMore: "Show More",
                  }}
                />
              </div>
            </ResultWrapper>
          </div>
        </InstantSearch>
      </div>
    </SectionWrapper>
  );
};
